import { useEffect, useRef, useState } from 'react';
import { IBaseItem } from '@/utils/types/Items';
import { rarityColor } from '@/utils/rarity';
import { iconBackgroundStyle } from '@/utils/iconBackground';

interface Props {
    items: IBaseItem[];
    selectedItemIds: Set<string | number | bigint>;
    onToggle: (id: string | number | bigint) => void;
    page: number;
    setPage: (n: number) => void;
    pageSize?: number;
    loading?: boolean;
    emptyMessage?: string;
}

// Geometry shared with the grid markup below — keep in sync if those change.
const CARD_MIN_W = 150;
const CARD_MIN_H = 178;
const GRID_GAP = 10; // gap-2.5
const GRID_PADDING = 12; // p-1.5 → 6px each side

const SkinGrid = ({
    items,
    selectedItemIds,
    onToggle,
    page,
    setPage,
    pageSize: fallbackPageSize = 32,
    loading,
    emptyMessage = 'No items found',
}: Props) => {
    const gridRef = useRef<HTMLDivElement>(null);
    const [autoPageSize, setAutoPageSize] = useState<number | null>(null);

    const pageSize = autoPageSize ?? fallbackPageSize;
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const pageItems = items.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => {
        if (page > totalPages) setPage(1);
    }, [totalPages, page, setPage]);

    // Pack rows × cols into the visible grid box so every page fills the viewport
    // and there's no half-empty trailing row.
    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;

        const compute = () => {
            const innerW = el.clientWidth - GRID_PADDING;
            const innerH = el.clientHeight - GRID_PADDING;
            if (innerW <= 0 || innerH <= 0) return;
            const cols = Math.max(1, Math.floor((innerW + GRID_GAP) / (CARD_MIN_W + GRID_GAP)));
            const rows = Math.max(1, Math.floor((innerH + GRID_GAP) / (CARD_MIN_H + GRID_GAP)));
            const next = cols * rows;
            setAutoPageSize((prev) => (prev === next ? prev : next));
        };

        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div className="surface flex flex-col gap-3.5 p-4 flex-1 min-h-0 overflow-hidden">
            <div
                ref={gridRef}
                className="fluent-scrollbar grid gap-2.5 flex-1 min-h-0 overflow-y-auto overflow-x-hidden content-start [scrollbar-gutter:stable] p-1.5"
                style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_W}px, 1fr))` }}
            >
                {loading ? (
                    Array.from({ length: 12 }).map((_, i) => (
                        <div
                            key={`shimmer-${i}`}
                            className="bg-layer_2 border border-stroke rounded-fl-md min-h-[178px] animate-pulse"
                        />
                    ))
                ) : pageItems.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center justify-center gap-3 text-center text-text_3 py-20 text-sm font-chat-msg">
                        <svg
                            viewBox="0 0 24 24"
                            className="w-10 h-10 stroke-current fill-none [stroke-width:1.5] opacity-30"
                        >
                            <circle cx="11" cy="11" r="7" />
                            <path d="m21 21-4.3-4.3" />
                        </svg>
                        {emptyMessage}
                    </div>
                ) : (
                    pageItems.map((item) => {
                        const rarity = rarityColor(item.colorHex);
                        const selected = selectedItemIds.has(item.id);
                        return (
                            <button
                                key={String(item.id)}
                                onClick={() => onToggle(item.id)}
                                style={
                                    {
                                        ['--rarity' as string]: rarity,
                                        background: selected
                                            ? `linear-gradient(160deg, color-mix(in srgb, ${rarity} 24%, transparent), color-mix(in srgb, ${rarity} 6%, transparent))`
                                            : `linear-gradient(160deg, color-mix(in srgb, ${rarity} 11%, transparent), color-mix(in srgb, ${rarity} 2%, transparent))`,
                                        borderColor: selected
                                            ? rarity
                                            : `color-mix(in srgb, ${rarity} 22%, transparent)`,
                                        boxShadow: selected
                                            ? `inset 0 0 22px -6px ${rarity}, 0 0 0 1.5px ${rarity}, 0 0 26px -6px ${rarity}`
                                            : undefined,
                                    } as React.CSSProperties
                                }
                                className="group relative border rounded-fl-md flex flex-col items-center justify-center cursor-pointer transition-all min-h-[178px] p-4 hover:-translate-y-0.5 hover:[border-color:var(--rarity)] hover:shadow-[inset_0_0_18px_-4px_var(--rarity),0_0_0_1.5px_var(--rarity)]"
                            >
                                <span
                                    className="absolute top-2.5 left-2.5 w-1.5 h-1.5 rounded-full pointer-events-none"
                                    style={{ background: rarity, boxShadow: `0 0 6px ${rarity}` }}
                                />
                                {item.icon ? (
                                    <img
                                        src={item.icon}
                                        alt=""
                                        draggable={false}
                                        className="w-24 h-24 max-md:w-16 max-md:h-16 object-contain mb-2.5 transition-transform group-hover:scale-105"
                                        style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.45))', ...iconBackgroundStyle(item.iconBackground) }}
                                    />
                                ) : (
                                    <div className="w-24 h-24 max-md:w-16 max-md:h-16 bg-layer_3 rounded mb-2.5" />
                                )}
                                <div className="font-chat-nick text-[15px] font-extrabold text-accent_5 tabular-nums leading-none">
                                    ${item.priceUsd.toFixed(2)}
                                </div>
                                <div className="font-chat-msg text-[11.5px] text-text_2 mt-1.5 text-center truncate w-full">
                                    {item.name}
                                </div>
                                {selected && (
                                    <div
                                        className="absolute top-2 right-2 w-5 h-5 rounded-full grid place-items-center text-white"
                                        style={{
                                            background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                            boxShadow: '0 0 12px rgba(139,109,255,0.7)',
                                        }}
                                    >
                                        <svg
                                            viewBox="0 0 24 24"
                                            className="w-3 h-3 stroke-current fill-none [stroke-width:3] [stroke-linecap:round] [stroke-linejoin:round]"
                                        >
                                            <path d="M5 12l4 4 10-10" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
            {totalPages > 1 && (
                <Pager page={page} setPage={setPage} totalPages={totalPages} />
            )}
        </div>
    );
};

const Pager = ({
    page,
    setPage,
    totalPages,
}: {
    page: number;
    setPage: (n: number) => void;
    totalPages: number;
}) => {
    const max = 6;
    const half = Math.floor(max / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);

    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);

    return (
        <div className="flex justify-center items-center gap-1.5 pt-3.5 border-t border-stroke">
            <PgButton onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} aria-label="Prev">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round]">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
            </PgButton>
            {start > 1 && (
                <>
                    <PgButton onClick={() => setPage(1)}>1</PgButton>
                    {start > 2 && <span className="text-text_4 px-1">…</span>}
                </>
            )}
            {pages.map((p) => (
                <PgButton key={p} active={p === page} onClick={() => setPage(p)}>
                    {p}
                </PgButton>
            ))}
            {end < totalPages && (
                <>
                    {end < totalPages - 1 && <span className="text-text_4 px-1">…</span>}
                    <PgButton onClick={() => setPage(totalPages)}>{totalPages}</PgButton>
                </>
            )}
            <PgButton
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                aria-label="Next"
            >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round]">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </PgButton>
        </div>
    );
};

const PgButton = ({
    children,
    onClick,
    disabled,
    active,
    ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        {...rest}
        className={`font-chat-nick min-w-9 h-9 px-3 rounded-fl text-[12.5px] font-extrabold inline-flex items-center justify-center transition-all ${
            active
                ? 'text-white border border-transparent -translate-y-px'
                : 'bg-layer_2 border border-stroke text-text_2 hover:bg-layer_3 hover:border-stroke_strong hover:text-text_main disabled:opacity-35 disabled:cursor-not-allowed'
        }`}
        style={
            active
                ? {
                      background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                      boxShadow:
                          'inset 0 1px 0 rgba(255,255,255,0.35), 0 6px 16px -4px rgba(139,109,255,0.6)',
                  }
                : undefined
        }
    >
        {children}
    </button>
);

export default SkinGrid;
